import './TextToneAndHashtagSelector.css';
import { useTranslation } from 'react-i18next';
import bookopenSVG from './Iconos/book-open.svg';
import briefcaseSVG from './Iconos/briefcase.svg';
import smileSVG from './Iconos/smile-2.svg';
import trending from './Iconos/trending-up.svg';
import musicSVG from './Iconos/music.svg';
import hashSVG from './Iconos/hash.svg';
import searchSVG from './Iconos/search.svg';
import flechaSVG from './Iconos/Flecha.svg';
import axios from 'axios';
import { useState, useEffect } from 'react';

type TextToneAndHashtagSelectorProps = {
  onToneChange: (tone: string) => void;
  onHashtagAdd: (hashtag: string) => void;
  selectedTone: string;
  hashtags: string[];
  addHashtagToText: (hashtag: string) => void;
};

const VALID_MINUTES = 30;

function TextToneAndHashtagSelector({
  onToneChange,
  onHashtagAdd,
  selectedTone,
  hashtags,
  addHashtagToText,
}: TextToneAndHashtagSelectorProps) {
  const [inputTopic, setInputTopic] = useState('');
  const [mainHashtags, setMainHashtags] = useState<string[]>([]);
  const [secondaryHashtags, setSecondaryHashtags] = useState<string[]>([]);
  const [mainIndex, setMainIndex] = useState(0);
  const [secondaryIndex, setSecondaryIndex] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const storedHashtags = JSON.parse(localStorage.getItem('hashtags') || '{}');
    const timestamp = storedHashtags.timestamp;
    const now = new Date().getTime();
    if (timestamp && (now - timestamp) < VALID_MINUTES * 60 * 1000) {
      setMainHashtags(storedHashtags.mainHashtags || []);
      setSecondaryHashtags(storedHashtags.secondaryHashtags || []);
    }
  }, []);

  useEffect(() => {
    if (mainHashtags.length > 0 || secondaryHashtags.length > 0) {
      const hashtagsData = {
        timestamp: new Date().getTime(),
        mainHashtags,
        secondaryHashtags
      };
      localStorage.setItem('hashtags', JSON.stringify(hashtagsData));
    }
  }, [mainHashtags, secondaryHashtags]);

  const fetchHashtags = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'; // En desarrollo puedes usar localhost por defecto

      console.log(backendUrl);

      const response = await axios.post(`${backendUrl}/generate-hashtags`, {
        tema: inputTopic
      });
      const hashtags = response.data;
      setMainHashtags(hashtags.slice(0, 5)); // Primeros 5 hashtags
      setSecondaryHashtags(hashtags.slice(5, 10)); // Últimos 5 hashtags
      setMainIndex(0); // Reset index
      setSecondaryIndex(0); // Reset index
    } catch (error) {
      console.error("Error fetching hashtags:", error);
    }
  };

  const handleMainLeft = () => {
    setMainIndex((prevIndex) => (prevIndex - 2 + mainHashtags.length) % mainHashtags.length);
  };

  const handleMainRight = () => {
    setMainIndex((prevIndex) => (prevIndex + 2) % mainHashtags.length);
  };

  const handleSecondaryLeft = () => {
    setSecondaryIndex((prevIndex) => (prevIndex - 2 + secondaryHashtags.length) % secondaryHashtags.length);
  };

  const handleSecondaryRight = () => {
    setSecondaryIndex((prevIndex) => (prevIndex + 2) % secondaryHashtags.length);
  };

  return (
    <div className="tones-container">
      <div>
        <div className='text-tone'>
          <div>  <img src={musicSVG} alt="Creativo" className="tone-icon" /></div>
          <div className='tone-text'>{t('Text-tone')}</div>
        </div>
        <div className="tones-row">
          <button
            className={`tone-button creativo ${selectedTone === 'creativo' ? 'active' : ''}`}
            onClick={() => onToneChange('creativo')}
          >
            <img src={bookopenSVG} alt="Creativo" className="tone-icon" />
            {t('Creativo')}
          </button>
          <button
            className={`tone-button negocio ${selectedTone === 'negocio' ? 'active' : ''}`}
            onClick={() => onToneChange('negocio')}
          >
            <img src={trending} alt="Negocio" className="tone-icon" />
            {t('Negocio')}
          </button>
        </div>
        <div className="tones-row">
          <button
            className={`tone-button infantil ${selectedTone === 'infantil' ? 'active' : ''}`}
            onClick={() => onToneChange('infantil')}
          >
            <img src={smileSVG} alt="Infantil" className="tone-icon" />
            {t('Infantil')}
          </button>
          <button
            className={`tone-button formal ${selectedTone === 'formal' ? 'active' : ''}`}
            onClick={() => onToneChange('formal')}
          >
            <img src={briefcaseSVG} alt="Formal" className="tone-icon" />
            {t('Formal')}
          </button>
        </div>
      </div>
      <div className='text-tone'>
        <div>  <img src={hashSVG} alt="Creativo" className="tone-icon" /></div>
        <div className='tone-text'>{t('Hashtag-Selector')}</div>
      </div>
      <div className="hashtags-container">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Algo que destaque del post..."
            value={inputTopic}
            onChange={e => setInputTopic(e.target.value)}
          />
          <button className="search-btn" onClick={fetchHashtags}>
            <img src={searchSVG} alt="Search" />
          </button>
        </div>
        <div className="hashtag-categories">
          <div className="category">
            <h4>Principales</h4>
            <div className="hashtag-navigation">
              <button onClick={handleMainLeft}><img src={flechaSVG} alt="Left" className="left-arrow" /></button>
              <div className="hashtags-list">
                {mainHashtags.slice(mainIndex, mainIndex + 2).map((hashtag, index) => (
                  <span key={index} className="hashtag" onClick={() => addHashtagToText(hashtag)}>{hashtag}</span>
                ))}
              </div>
              <button onClick={handleMainRight}><img src={flechaSVG} alt="Right" className="right-arrow" /></button>
            </div>
          </div>
          <div className="category">
            <h4>Secundarias</h4>
            <div className="hashtag-navigation">
              <button onClick={handleSecondaryLeft}><img src={flechaSVG} alt="Left" className="left-arrow" /></button>
              <div className="hashtags-list">
                {secondaryHashtags.slice(secondaryIndex, secondaryIndex + 2).map((hashtag, index) => (
                  <span key={index} className="hashtag" onClick={() => addHashtagToText(hashtag)}>{hashtag}</span>
                ))}
              </div>
              <button onClick={handleSecondaryRight}><img src={flechaSVG} alt="Right" className="right-arrow" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TextToneAndHashtagSelector;
